
import { useTranslation } from "react-i18next";
import { ScheduleHeader } from "./components/ScheduleHeader";
import { ScheduleContent } from "@/components/schedule/ScheduleContent";
import { useSchedulePageState } from "./hooks/useSchedulePageState";
import { AddClassDialog } from "@/components/schedule/AddClassDialog";
import { useDraftResumeToast } from "@/hooks/useDraftResumeToast";
import { useUserStore } from "@/store/userStore";
import { useThemeCustomization } from "@/hooks/useThemeCustomization";
import { AthleteHeroBackground } from "@/components/athlete/AthleteHeroBackground";

const SchedulePage = () => {
  const { t } = useTranslation(["schedule", "common", "bookingui"]);
  const role = useUserStore((state) => state.role);
  const { athleteTheme, athleteBackgrounds } = useThemeCustomization();
  const featuredCoach = role === "coach" && athleteTheme.variant === "dark-premium";
  const {
    selectedDate,
    currentMonth,
    viewMode,
    weekDates,
    selectedCalendarId,
    setSelectedCalendarId,
    classes,
    isLoading,
    handleDateSelect,
    handleMonthChange,
    handlePrevious,
    handleNext,
    handleAddClass,
    fetchClasses,
    dialogState
  } = useSchedulePageState();

  // Création de séance interrompue par un reload → proposer de la reprendre
  // (l'heure vient du créneau : elle n'est pas brouillonnée, le dialog
  // s'ouvre avec l'heure par défaut).
  useDraftResumeToast([
    {
      formId: "add-class",
      message: t("draft.classUnsaved", { ns: "bookingui" }),
      description: (d) => (d.name as string) || undefined,
      isEmpty: (d) => !(d.name || d.courseId || d.instructor),
      onResume: () => dialogState.setAddClassDialogOpen(true),
    },
  ]);

  return (
    <div className="min-h-screen flex flex-col w-full">
      <main className="mx-auto w-full max-w-7xl flex-1 animate-fade-in p-3 md:p-6">
        {featuredCoach ? (
          <section className="coach-schedule-hero relative mb-3 overflow-hidden">
            <AthleteHeroBackground
              src={athleteBackgrounds.schedule}
              className="absolute inset-0 h-full w-full object-cover object-[center_28%]"
            />
            <div className="coach-hero-overlay absolute inset-0" />
            <div className="relative z-10 flex min-h-28 flex-col justify-end p-4 md:min-h-36 md:p-5">
              <h1 className="text-2xl font-extrabold md:text-3xl">{t('title', { ns: 'schedule' })}</h1>
              <p className="mt-0.5 text-sm text-muted-foreground md:text-base">{t('subtitle', { ns: 'schedule' })}</p>
            </div>
          </section>
        ) : (
          <ScheduleHeader title={t('title', { ns: 'schedule' })} subtitle={t('subtitle', { ns: 'schedule' })} />
        )}

        <div className="flex flex-col gap-4">
          <ScheduleContent
            weekDates={weekDates}
            selectedDate={selectedDate}
            onPrevious={handlePrevious}
            onNext={handleNext}
            onDateSelect={handleDateSelect}
            onAddClass={handleAddClass}
            onEditClass={() => {}}
            currentMonth={currentMonth}
            onMonthChange={handleMonthChange}
            classes={classes}
            isLoading={isLoading}
            refreshClasses={fetchClasses}
            selectedCalendarId={selectedCalendarId}
            setSelectedCalendarId={setSelectedCalendarId}
          />
        </div>
      </main>
      <AddClassDialog
        isOpen={dialogState.addClassDialogOpen}
        onOpenChange={dialogState.setAddClassDialogOpen}
        initialDate={dialogState.selectedDate}
        initialTime={dialogState.selectedTime}
        onSave={dialogState.handleSaveClass}
        initialCalendarId={selectedCalendarId}
      />
    </div>
  );
};

export default SchedulePage;
